type FetchRequest<T> = Parameters<typeof $fetch<ApiResponse<T>>>[0]
type FetchOptions<T> = Parameters<typeof $fetch<ApiResponse<T>>>[1] & {
  type?: UserType
  delay?: boolean
}

let refreshTokenPromise: Promise<boolean> | null = null

async function refreshToken(opts?: FetchOptions<any>): Promise<boolean> {
  const { updateToken, getRefreshToken } = useAuth()
  if (!getRefreshToken()) return false
  if (!refreshTokenPromise) {
    refreshTokenPromise = (async () => {
      try {
        const refreshUrl =
          opts?.type === UserType.STREAMER ? '/user/refresh' : '/users/refresh'
        const res = await $fetch<ApiResponse<LoginResponse>>(
          `${refreshUrl}?refreshToken=${getRefreshToken()}`,
          {
            ...opts,
            method: 'POST',
          },
        )
        if (res.code === ApiStatusCode.SUCCESS) {
          updateToken(res.data)
          return true
        }
      } catch (err) {
        console.error('🔴 Refresh token failed:', err)
      } finally {
        refreshTokenPromise = null
      }
      return false
    })()
  }

  return refreshTokenPromise
}

export async function $api<T>(
  request: FetchRequest<T>,
  opts?: FetchOptions<T>,
) {
  const isUnauthorized = useState('isUnauthorized', () => false)
  const { getToken, logout, user, handleUserDeactivation } = useAuth()
  const config = useRuntimeConfig()

  const getRequestOptions = (): FetchOptions<T> => ({
    baseURL:
      opts?.type === UserType.STREAMER
        ? config.public.cmsApiBase
        : config.public.apiBase,
    retry: false,
    ...opts,
    headers: omitEmpty({
      Authorization: getToken() && !opts?.baseURL ? `Bearer ${getToken()}` : '',
      'X-DEVICE-UDID': user.value?.deviceUDID ?? '',
      'X-DEVICE-TYPE': String(user.value?.deviceType ?? ''),
      ...opts?.headers,
    }),
  })

  const unauthorized = async () => {
    if (isUnauthorized.value) return
    isUnauthorized.value = true
    logout()
    if (opts?.type === UserType.STREAMER) {
      await navigateTo('/login')
    } else {
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  }

  const doRequest = async (): Promise<T> => {
    return $fetch<ApiResponse<T>>(request, getRequestOptions()).then((res) => {
      if (res.code === ApiStatusCode.SUCCESS) {
        isUnauthorized.value = false
        return res.data
      } else if (
        res.code === ApiStatusCode.REMOTE_LOGIN &&
        !isUnauthorized.value
      ) {
        unauthorized()
        showToastOnce(res.message, res.code)
      } else if (res.code === ApiStatusCode.ACCOUNT_DEACTIVE) {
        isUnauthorized.value = true
        handleUserDeactivation()
      }
      throw res as ApiErrorResponse
    })
  }

  try {
    const [_, result] = await Promise.all([
      opts?.delay ? sleep(500) : sleep(0),
      doRequest(),
    ])
    return result
  } catch (error: any) {
    if (error?.response?.status === HttpStatusCode.Unauthorized) {
      const refreshed = await refreshToken(getRequestOptions())
      if (!refreshed) {
        await unauthorized()
      }
    }
    throw error
  }
}

export function feedbackOnError(error: ErrorResponse, defaultMessage: string) {
  if ('code' in error) {
    showToastOnce(
      API_ERROR_MESSAGE[error.code] || error?.message || defaultMessage,
      error?.code,
    )
  } else {
    showToastOnce(defaultMessage, 'general-error')
  }
}
